import { defineStore } from 'pinia';
import { StoreId } from '@/modules/common/enums/store-id.enum.ts';
import UpdatedFile from '@/modules/document/models/file/updated-file.ts';
import { FileEditorScope } from '@/modules/common/enums/file-editor-scope.ts';
import { Route } from '@/modules/core/enums/route.enum.ts';
import type { Router } from 'vue-router';
import type TemplateFile from '@/modules/document/models/file/template-file.ts';
import type DocumentFile from '@/modules/document/models/file/document-file.ts';
import type { RequiredKey } from '@/modules/common/types/utils.ts';

export type EditorFile = {
  id: string;
  fileId: number;
  documentId: number;
  name: string;
  type: string;
  timestamp: number;
  scope: FileEditorScope;
  isLoaded: boolean;
  url: string;
  isOpen: boolean;
  updatedUrl?: string;
};

export type UpdatedEditorFile = RequiredKey<EditorFile, 'updatedUrl'>;

type State = {
  files: EditorFile[];
};

export const useFileEditorStore = defineStore(StoreId.FileEditor, {
  state: (): State => ({
    files: [],
  }),
  getters: {
    isFileOpen(state) {
      return (file: EditorFile) =>
        state.files.some(
          (f) => f.documentId === file.documentId && f.name === file.name && f.timestamp !== file.timestamp
        );
    },
    getFile(state) {
      return (id: string, timestamp: number) =>
        state.files.find((file) => file.id === id && file.timestamp === timestamp);
    },
    getUpdatedFiles(state) {
      return (scope: FileEditorScope) =>
        state.files
          .filter((file): file is UpdatedEditorFile => file.scope === scope && !!file.updatedUrl)
          .map((file) => new UpdatedFile(file));
    },
  },
  actions: {
    addFile(
      router: Router,
      newFile: DocumentFile | TemplateFile,
      isLoaded = false,
      scope = FileEditorScope.Global,
      documentId = newFile.documentId
    ) {
      const id = newFile.id.toString();
      const timestamp = Date.now();

      this.files = [
        ...this.files,
        {
          id,
          fileId: newFile.fileId,
          documentId,
          name: newFile.name,
          type: newFile.type,
          timestamp,
          scope,
          isLoaded,
          isOpen: false,
          url: newFile.url,
        },
      ];

      const route = router.resolve({
        name: Route.FileEditorPage,
        params: { id },
        query: { t: timestamp },
      });

      window.open(route.href, '_blank');
    },
    setFileLoaded(id: string, isLoaded: boolean) {
      this.files = this.files.map((file) => (file.id === id ? { ...file, isLoaded } : file));
    },
    setFileUpdatedUrl(id: string, timestamp: number, updatedUrl: string | undefined) {
      const file = this.files.find((file) => file.id === id && file.timestamp === timestamp);

      if (!file) {
        throw new Error('Error setting link to edited file.');
      }

      this.files = [
        { ...file, updatedUrl },
        ...this.files.filter((file) => file.id !== id || file.timestamp !== timestamp),
      ];
    },
    clearFileUpdatedUrl(id: string, timestamp: number) {
      this.setFileUpdatedUrl(id, timestamp, undefined);
    },
    setFileState(id: string, timestamp: number, isOpen: boolean) {
      this.files = this.files.map((file) =>
        file.id === id && file.timestamp === timestamp ? { ...file, isOpen } : file
      );
    },
    removeUnusedFiles() {
      this.files = this.files.filter((file) => file.isOpen || file.updatedUrl);
    },
    clearScope(scope = FileEditorScope.Global) {
      this.files = this.files.filter((file) => file.scope !== scope);
    },
  },
  persist: true,
});
